import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import * as DOMPurify from 'dompurify';
import { ConversationMessage, InboxAdapter, InboxRowItem } from './types';
import { TYPE_META, companyInitials, IconButton } from './InboxCard';
import appState from '../../state/AppStateContainer';
import BtButton from '../bt-button/bt-button';
import styles from './InboxPreviewPanel.module.scss';

// Render the message cleanly: drop the email CTA <center> block and all inline
// styles/classes so email-template spacing doesn't bloat the compact preview.
const sanitizeHTML = (html: string): string => {
  const stripped = html.replace(/<center[\s\S]*?<\/center>/gi, '');
  return DOMPurify.sanitize(stripped, { FORBID_ATTR: ['style', 'class'] });
};

// Pull the CTA link out of the email's orange button (getOrangeButton) so we can
// re-render it as a proper bt-button without keeping the email styling.
const extractCta = (html: string): { href: string; text: string } | null => {
  const m = html.match(/background-color:\s*#FF5C00[\s\S]*?<a\s+href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/i);
  if (!m) return null;
  const text = m[2].replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  return { href: m[1], text };
};

// A 4xx means the reply was not saved; any other failure leaves open whether it was.
const outcomeOf = (send: Promise<unknown>): Promise<'sent' | 'refused' | 'unknown'> =>
  send.then(
    () => 'sent' as const,
    (error: unknown) => {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      return status !== undefined && status >= 400 && status < 500 ? 'refused' as const : 'unknown' as const;
    },
  );


type InboxPreviewPanelProps<Item extends InboxRowItem> = {
  adapter: InboxAdapter<Item>;
  item: Item | null;
  onClose: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onCreateActivity?: () => void;
  onReplySent: () => void;
};

const InboxPreviewPanel = <Item extends InboxRowItem>({
  adapter, item, onClose, onMarkRead, onMarkUnread, onCreateActivity, onReplySent,
}: InboxPreviewPanelProps<Item>) => {
  const [t] = useTranslation('dashboard');
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sendFailure, setSendFailure] = useState<'notSent' | 'maybeSent' | null>(null);
  const [conversation, setConversation] = useState<ConversationMessage[] | null>(null);
  const [convLoading, setConvLoading] = useState(false);
  const [newMsgIds, setNewMsgIds] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pendingAnchorScrollRef = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // Close on click outside the panel — uses mousedown so scroll events are unaffected.
  useEffect(() => {
    if (!item) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onCloseRef.current();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [item]);

  // Auto-mark-read after a short dwell, so arrowing PAST a conversation doesn't
  // mark it read (and fire a mark-read PUT + notification refetch) on transit —
  // only landing on one for ~half a second does. Changing selection (or closing)
  // cancels the pending timer via the cleanup.
  useEffect(() => {
    if (!item || item.unreadCount === 0) return;
    const id = setTimeout(() => onMarkRead(), 500);
    return () => clearTimeout(id);
  }, [item?.conversationId]);

  useEffect(() => {
    setReplyText('');
    setReplyFiles([]);
    setSendFailure(null);
  }, [item?.conversationId]);

  useEffect(() => {
    if (!item) { setConversation(null); return; }
    // Capture unread state before onMarkRead mutates it, so we can mark "new" messages.
    const wasUnread = item.unreadCount > 0;
    const latestId = item.latestMsgId;
    setConversation(null);
    setNewMsgIds(new Set());
    setConvLoading(true);
    adapter.loadThread(item)
      .then(conv => {
        pendingAnchorScrollRef.current = true;
        setConversation(conv);
        const ids = new Set(conv.filter(m => m.isUnread).map(m => m._id));
        if (wasUnread && latestId) ids.add(latestId);
        setNewMsgIds(ids);
      })
      .catch(() => {})
      .finally(() => setConvLoading(false));
  }, [item?.conversationId]);

  // On open, jump (no animation) to the new message. Skipped after a reply
  // refetch so the post-send scroll-to-bottom isn't overridden.
  useEffect(() => {
    if (conversation && pendingAnchorScrollRef.current) {
      pendingAnchorScrollRef.current = false;
      (anchorElRef.current ?? bottomRef.current)?.scrollIntoView({ block: 'center' });
    }
  }, [conversation]);

  const orgUrl = appState._getCurrentOrgUrl();

  const replyArrived = () => {
    setReplyText('');
    setReplyFiles([]);
    onReplySent();
  };

  const handleSend = async () => {
    if (!item || (!replyText.trim() && replyFiles.length === 0)) return;
    // Taken before sending, so an unclear failure can tell whether the reply arrived anyway.
    const shownOwnIds = conversation && new Set(conversation.filter(msg => adapter.isOwnMessage(msg, item)).map(msg => msg._id));
    setSending(true);
    setSendFailure(null);
    try {
      const outcome = await outcomeOf(adapter.sendReply(item, replyText, replyFiles));
      if (outcome === 'refused') return setSendFailure('notSent');
      // The inbox-list refresh runs in parallel with the thread reload below.
      if (outcome === 'sent') replyArrived();
      const thread = await adapter.loadThread(item).catch(() => null);
      if (thread) setConversation(thread);
      if (outcome === 'unknown') {
        const ownReplyAppeared = !!thread && !!shownOwnIds
          && thread.some(msg => adapter.isOwnMessage(msg, item) && !shownOwnIds.has(msg._id));
        if (!ownReplyAppeared) return setSendFailure('maybeSent');
        replyArrived();
      }
      // Show the just-sent reply at the bottom.
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={panelRef} className={`${styles.panel} ${item ? styles.panelOpen : ''}`}>
        {item && (
          <>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerMain}>
                <div className={styles.headerTitleRow}>
                  <span className={styles.headerName}>
                    {adapter.counterpart(item) || item.title}
                  </span>
                  {(() => {
                    const typeInfo = TYPE_META[item.messageType] ?? TYPE_META.standardMessage;
                    return (
                      <span className={styles.headerTypeBadge} style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}>
                        {t(typeInfo.labelKey, typeInfo.fallback)}
                      </span>
                    );
                  })()}
                </div>
                <div className={styles.headerSub}>
                  {item.title && <span>{item.title}</span>}
                  {item.title && item.senderName && <span> · </span>}
                  {item.senderName && <span className={styles.headerSender}>{item.senderName}</span>}
                </div>
              </div>
              <div className={styles.headerActions}>
                <IconButton
                  onClick={item.unreadCount === 0 ? onMarkUnread : onMarkRead}
                  icon={item.unreadCount === 0 ? MailOutlineIcon : DraftsOutlinedIcon}
                  title={item.unreadCount === 0 ? t('inbox.markAsUnread', 'Markera som oläst') : t('inbox.markAsRead', 'Markera som läst')}
                  size={36} borderRadius={8} iconSize={18}
                />
                {onCreateActivity && (
                  <IconButton onClick={onCreateActivity} icon={AddTaskOutlinedIcon} title={t('inbox.createActivity', 'Skapa aktivitet')} size={36} borderRadius={8} iconSize={18} />
                )}
                <button onClick={onClose} title={t('inbox.preview.close', 'Stäng')} className={styles.closeBtn}>
                  ×
                </button>
              </div>
            </div>

            {/* Conversation thread */}
            <div className={styles.thread}>
              {convLoading && (
                <div className={styles.convLoading}>
                  {t('inbox.preview.loadingConversation', 'Laddar konversation...')}
                </div>
              )}
              {!convLoading && conversation && conversation.map((msg) => {
                const isOwn = adapter.isOwnMessage(msg, item);
                const senderName = msg.overrideInformation?.senderName
                  || msg.adminId?.name
                  || msg.supplierMemberId?.name
                  || '';
                const initials = companyInitials(senderName);
                const isNew = newMsgIds.has(msg._id);
                const isAnchor = msg._id === item.latestMsgId;
                const cta = extractCta(msg.message);
                const ctaTo = cta && !/^https?:\/\//i.test(cta.href) ? `${orgUrl}${cta.href}` : cta?.href;
                const ctaLabel = cta ? (cta.text || t('inbox.preview.open', 'Öppna')) : '';
                return (
                  <div
                    key={msg._id}
                    ref={isAnchor ? anchorElRef : undefined}
                    className={`${styles.msg} ${isOwn ? styles.msgAdmin : ''}`}
                  >
                    <div className={styles.msgHead}>
                      <div className={`${styles.msgAvatar} ${isOwn ? styles.msgAvatarAdmin : ''}`}>
                        {initials}
                      </div>
                      <span className={styles.msgSender}>{senderName}</span>
                      <div className={styles.msgMeta}>
                        {isNew && (
                          <span title={t('inbox.preview.new', 'Ny')} className={styles.newDot} />
                        )}
                        <span className={styles.msgTime}>
                          {moment(msg.createdAt).format('D MMM YYYY, HH:mm')}
                        </span>
                      </div>
                    </div>
                    <div
                      className={styles.messageBody}
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(msg.message) }}
                    />
                    {cta && ctaTo && (
                      /^https?:\/\//i.test(cta.href) ? (
                        <a href={ctaTo} target="_blank" rel="noreferrer" className={styles.ctaLink}>
                          <BtButton size="small">{ctaLabel}</BtButton>
                        </a>
                      ) : (
                        <Link to={ctaTo} className={styles.ctaLink}>
                          <BtButton size="small">{ctaLabel}</BtButton>
                        </Link>
                      )
                    )}
                    {msg.attachment?.length > 0 && (
                      <div className={styles.attachList}>
                        {msg.attachment.map((att, j) => (
                          <button
                            key={j}
                            onClick={() => adapter.downloadAttachment(item, att)}
                            className={styles.attachBtn}
                          >
                            <AttachFileIcon style={{ fontSize: 13, color: '#9ca3af' }} />
                            {att.fileName || att.file}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Reply */}
            <div className={styles.reply}>
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder={t('inbox.preview.replyPlaceholder', 'Skriv ett svar...')}
                rows={3}
                className={styles.replyTextarea}
              />
              {replyFiles.length > 0 && (
                <div className={styles.replyFiles}>
                  {replyFiles.map((f, i) => (
                    <span key={i} className={styles.replyFileChip}>
                      {f.name}
                      <button
                        onClick={() => setReplyFiles(prev => prev.filter((_, j) => j !== i))}
                        className={styles.replyFileRemove}
                      >
                        <CloseIcon style={{ fontSize: 12 }} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {sendFailure && (
                <p role="alert" className={styles.replyError}>
                  {sendFailure === 'maybeSent'
                    ? t('inbox.preview.replyMaybeSent', 'Svaret kan ha skickats. Kontrollera konversationen innan du skickar igen.')
                    : t('inbox.preview.replyNotSent', 'Svaret kunde inte skickas.')}
                </p>
              )}
              <div className={styles.replyActions}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  title={t('inbox.preview.attach', 'Bifoga fil')}
                  className={styles.attachButton}
                >
                  <AttachFileIcon style={{ fontSize: 16 }} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className={styles.hiddenInput}
                  onChange={e => {
                    if (e.target.files) setReplyFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                    e.target.value = '';
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={sending || (!replyText.trim() && replyFiles.length === 0)}
                  className={styles.sendBtn}
                  style={sending ? { cursor: 'wait' } : undefined}
                >
                  <SendIcon style={{ fontSize: 15 }} />
                  {sending ? t('inbox.preview.sending', 'Skickar...') : t('inbox.preview.send', 'Skicka')}
                </button>
              </div>
            </div>

            {(() => {
              const link = adapter.conversationLink?.(item);
              return link ? (
                <div className={styles.footer}>
                  <Link to={link} className={styles.footerLink}>
                    {t('inbox.goToConversation', 'Gå till konversation')} →
                  </Link>
                </div>
              ) : null;
            })()}
          </>
        )}
    </div>
  );
};

export default InboxPreviewPanel;
